import { Button, cn, useTranslation } from '@hbs-us/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { downloadCredentialed } from '../../lib/download.js';
import { VehicleCardGrid } from './components/VehicleCardGrid.js';
import type { FleetListResponse, FleetMaintenanceCategory } from './contractsMirror.js';
import { EntryFormDrawer } from './EntryFormDrawer.js';
import { fleetCsvExportUrl } from './exportLinks.js';
import type { FleetSearch } from './searchParams.js';

/**
 * The redesigned fleet list (UI-SPEC archetype A — card grid + detail) on the locked Velocity
 * brand. Page wrapper + header (title, subtitle, "+ Add Vehicle" CTA) + a labelled search input +
 * the entry-type / maintenance-category filter pills + the archived toggle + the vehicle CARD GRID
 * (one card per vehicle) — or the empty state.
 *
 * The coarse endpoint (22-09) owns the entry-type / maintenance-category / archived filtering
 * server-side; this view applies the free-text vehicle search IN-MEMORY over the already-loaded
 * card grid (small dataset, no extra round-trip). Each filter pill is a typed `<Link>` that flips
 * the URL search params and drives exactly one coarse-endpoint refetch (the reports `RangeFilter`
 * navigate pattern). The SPA never sends a tenant id (SEC-11).
 */

const MAINTENANCE_CATEGORIES: readonly FleetMaintenanceCategory[] = [
  'oil_change',
  'tire_rotation',
  'inspection',
  'brakes',
  'tires',
  'engine',
  'transmission',
  'cooling',
  'electrical',
  'other',
];

export interface FleetViewProps {
  data: FleetListResponse;
  search: FleetSearch;
}

function pillClass(isActive: boolean): string {
  return cn(
    'inline-flex h-9 items-center rounded-full border px-3 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    isActive
      ? 'border-transparent bg-secondary text-secondary-foreground'
      : 'border-border bg-card text-muted-foreground hover:bg-secondary',
  );
}

export function FleetView({ data, search }: FleetViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const vehicles = data.vehicles ?? [];
  const entries = data.entries ?? [];
  const dueReminders = data.dueReminders ?? [];
  const assignmentSummary = data.assignmentSummary ?? {
    assignedVehicles: 0,
    unassignedVehicles: 0,
  };
  const expiringDocuments = data.expiringDocuments ?? [];
  const showArchived = Boolean(search.archived);
  const activeEntryType = search.entryType ?? null;
  const activeMaintenanceCategory = search.maintenanceCategory ?? null;

  // Push a date-range filter into the URL (drives the coarse refetch + the CSV export). Preserving
  // the other active filters keeps the canonical URL coherent.
  function setDateRange(next: { dateFrom?: string; dateTo?: string }) {
    void navigate({
      to: '/fleet',
      search: {
        ...search,
        dateFrom: next.dateFrom || undefined,
        dateTo: next.dateTo || undefined,
      },
    });
  }

  const [queryText, setQueryText] = useState('');
  const [entryDrawerOpen, setEntryDrawerOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = queryText.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter((vehicle) => {
      const haystack = [
        vehicle.displayName,
        vehicle.unitNumber,
        vehicle.licensePlate,
        vehicle.make,
        vehicle.model,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [vehicles, queryText]);

  const hasData = filtered.length > 0;
  const searching = queryText.trim().length > 0;
  // The query is filtered server-side, so an active filter can empty the result set. Filters
  // stay visible while one is active, otherwise the user is stranded with no way to clear it.
  const hasActiveFilter = Boolean(
    showArchived ||
      activeEntryType ||
      activeMaintenanceCategory ||
      search.dateFrom ||
      search.dateTo,
  );
  const hasFilterableData = vehicles.length > 0 || entries.length > 0 || hasActiveFilter;

  // Preserve the other active filters when a pill flips one dimension.
  const baseSearch: FleetSearch = showArchived ? { archived: true } : {};

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {showArchived ? t('fleet:archivedTitle') : t('fleet:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {showArchived ? t('fleet:archivedSubtitle') : t('fleet:subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="secondary">
            <Link to="/fleet/schedule">{t('fleet:actions.schedule')}</Link>
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              void downloadCredentialed(fleetCsvExportUrl(search), 'fleet-report.csv').catch(
                (err) => console.error('fleet CSV export failed', err),
              )
            }
          >
            {t('fleet:actions.exportCsv')}
          </Button>
          {/* An entry needs a vehicle; a greyed-out button with no reason read as broken. */}
          {vehicles.length > 0 ? (
            <Button variant="secondary" onClick={() => setEntryDrawerOpen(true)}>
              {t('fleet:actions.logEntry')}
            </Button>
          ) : null}
          <Button onClick={() => void navigate({ to: '/fleet/vehicles/new' })}>
            {t('fleet:actions.addVehicle')}
          </Button>
        </div>
      </header>

      {/* Widgets: due reminders · assignment summary · expiring documents (F1/F3/F4). */}
      <section
        aria-label={t('fleet:widgets.label')}
        className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3"
      >
        <div className="rounded-md border border-border bg-card p-4">
          <h2 className="font-display font-semibold text-foreground text-sm">
            {t('fleet:widgets.dueReminders')}
          </h2>
          {dueReminders.length > 0 ? (
            <ul className="mt-2 space-y-1 text-sm">
              {dueReminders.slice(0, 5).map((reminder) => (
                <li key={`${reminder.vehicleId}-${reminder.label}`} className="text-foreground">
                  <span className="font-medium">{reminder.vehicleName}</span>
                  <span className="text-muted-foreground"> · {reminder.label}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-muted-foreground text-sm">{t('fleet:widgets.noReminders')}</p>
          )}
        </div>

        <div className="rounded-md border border-border bg-card p-4">
          <h2 className="font-display font-semibold text-foreground text-sm">
            {t('fleet:widgets.assignments')}
          </h2>
          <dl className="mt-2 space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">{t('fleet:widgets.assigned')}</dt>
              <dd className="font-medium text-foreground">{assignmentSummary.assignedVehicles}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">{t('fleet:widgets.unassigned')}</dt>
              <dd className="font-medium text-foreground">
                {assignmentSummary.unassignedVehicles}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-md border border-border bg-card p-4">
          <h2 className="font-display font-semibold text-foreground text-sm">
            {t('fleet:widgets.expiringDocuments')}
          </h2>
          {expiringDocuments.length > 0 ? (
            <ul className="mt-2 space-y-1 text-sm">
              {expiringDocuments.slice(0, 5).map((doc) => (
                <li key={doc.id} className="text-foreground">
                  <span className="font-medium">{doc.vehicleDisplayName}</span>
                  <span className="text-muted-foreground"> · {doc.title}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-muted-foreground text-sm">{t('fleet:widgets.noExpiring')}</p>
          )}
        </div>
      </section>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="fleet-search" className="sr-only">
            {t('fleet:search.label')}
          </label>
          <div className="relative w-full max-w-md sm:w-80">
            <Search
              className="pointer-events-none absolute top-3.5 left-3 size-4 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              id="fleet-search"
              type="search"
              value={queryText}
              onChange={(event) => setQueryText(event.target.value)}
              placeholder={t('fleet:search.placeholder')}
              className="h-11 w-full rounded-md border border-border bg-card pr-3 pl-9 text-foreground text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            />
          </div>
        </div>
        {hasFilterableData ? (
          <>
            <div>
              <label
                htmlFor="fleet-date-from"
                className="mb-1 block font-medium text-foreground text-sm"
              >
                {t('fleet:filter.dateFrom')}
              </label>
              <input
                id="fleet-date-from"
                type="date"
                value={search.dateFrom ?? ''}
                onChange={(event) =>
                  setDateRange({ dateFrom: event.target.value, dateTo: search.dateTo })
                }
                className="h-11 rounded-md border border-border bg-card px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
            </div>
            <div>
              <label
                htmlFor="fleet-date-to"
                className="mb-1 block font-medium text-foreground text-sm"
              >
                {t('fleet:filter.dateTo')}
              </label>
              <input
                id="fleet-date-to"
                type="date"
                value={search.dateTo ?? ''}
                onChange={(event) =>
                  setDateRange({ dateFrom: search.dateFrom, dateTo: event.target.value })
                }
                className="h-11 rounded-md border border-border bg-card px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
            </div>
          </>
        ) : null}
      </div>

      {/* Entry-type filter pills (drive the coarse refetch). */}
      <section
        aria-label={t('fleet:filter.entryTypeLabel')}
        className="mb-3 flex flex-wrap items-center gap-2"
      >
        {hasFilterableData ? (
          <>
            <Link to="/fleet" search={baseSearch} className={pillClass(activeEntryType === null)}>
              {t('fleet:filter.allEntries')}
            </Link>
            <Link
              to="/fleet"
              search={{ ...baseSearch, entryType: 'fuel' }}
              className={pillClass(activeEntryType === 'fuel')}
            >
              {t('fleet:entryType.fuel')}
            </Link>
            <Link
              to="/fleet"
              search={{ ...baseSearch, entryType: 'maintenance' }}
              className={pillClass(activeEntryType === 'maintenance')}
            >
              {t('fleet:entryType.maintenance')}
            </Link>
          </>
        ) : null}

        {/* Never hidden: with nothing archived the view is empty, and this is the way back. */}
        <Link
          to="/fleet"
          search={showArchived ? {} : { archived: true }}
          className="ml-auto inline-flex h-9 items-center rounded-md border border-border bg-card px-3 font-medium text-foreground text-sm transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {showArchived ? t('fleet:filter.hideArchived') : t('fleet:filter.showArchived')}
        </Link>
      </section>

      {/* Maintenance-category filter pills — only meaningful for maintenance entries. */}
      {hasFilterableData ? (
        <section
          aria-label={t('fleet:filter.maintenanceCategoryLabel')}
          className="mb-6 flex flex-wrap items-center gap-2"
        >
          <Link
            to="/fleet"
            search={activeEntryType ? { ...baseSearch, entryType: activeEntryType } : baseSearch}
            className={pillClass(activeMaintenanceCategory === null)}
          >
            {t('fleet:filter.allCategories')}
          </Link>
          {MAINTENANCE_CATEGORIES.map((category) => (
            <Link
              key={category}
              to="/fleet"
              search={
                activeEntryType
                  ? { ...baseSearch, entryType: activeEntryType, maintenanceCategory: category }
                  : { ...baseSearch, maintenanceCategory: category }
              }
              className={pillClass(activeMaintenanceCategory === category)}
            >
              {t(`fleet:maintenanceCategory.${category}`)}
            </Link>
          ))}
        </section>
      ) : null}

      {hasData ? (
        <VehicleCardGrid vehicles={filtered} />
      ) : (
        <div className="rounded-md border border-border border-dashed bg-card p-12 text-center">
          <h2 className="font-display font-semibold text-foreground text-lg">
            {searching
              ? t('fleet:empty.searchHeading')
              : showArchived
                ? t('fleet:empty.archivedHeading')
                : t('fleet:empty.heading')}
          </h2>
          <p className="mt-1 text-muted-foreground text-sm">
            {searching
              ? t('fleet:empty.searchBody')
              : showArchived
                ? t('fleet:empty.archivedBody')
                : t('fleet:empty.body')}
          </p>
        </div>
      )}

      <EntryFormDrawer
        open={entryDrawerOpen}
        mode="create"
        vehicles={vehicles}
        onClose={() => setEntryDrawerOpen(false)}
      />
    </div>
  );
}
